/**
 * The longest prefix of `text` that fits `maxWidth`, with an ellipsis marking what was dropped.
 *
 * SVG text has no `text-overflow`, so a label too wide for its node was simply cut by the clip path
 * — "CHEMOURS_EAST_WING_" reads as a whole name rather than as a fragment. Measuring is the only way
 * to know where to cut, and `measure` is injected so the arithmetic can be tested without a browser:
 * the component passes one backed by `getComputedTextLength`.
 *
 * Returns `text` unchanged when it already fits, so a short name renders exactly as before.
 */
export function truncateLabelToWidth(text: string, maxWidth: number, measure: (candidate: string) => number): string {
  if (measure(text) <= maxWidth) {
    return text
  }

  /*
   * Characters, not code units. Slicing a string cuts between the halves of a surrogate pair and
   * leaves a lone surrogate, which renders as a replacement character — so a name ending in an emoji
   * would be truncated into a broken glyph rather than a shorter name.
   */
  const characters = [...text]

  let fits = 0
  let tooLong = characters.length

  while (fits < tooLong) {
    const midpoint = Math.ceil((fits + tooLong) / 2)

    if (measure(`${characters.slice(0, midpoint).join('')}…`) <= maxWidth) {
      fits = midpoint
    } else {
      tooLong = midpoint - 1
    }
  }

  // Even the ellipsis alone can be too wide, on a node narrower than any label; the clip path is
  // still behind this and will cut what is left.
  return `${characters.slice(0, fits).join('')}…`
}
